// WS-Client für die Web-UI: verbindet sich mit dem API-WebSocket und empfängt Events.

import { getAccessToken } from "./api";

export type WSHandler = (event: string, payload: Record<string, unknown>) => void;

let socket: WebSocket | null = null;
let pingTimer: number | null = null;
let reconnectTimer: number | null = null;
let attempts = 0;
const handlers = new Set<WSHandler>();
const subscribedChannels = new Set<string>();
let explicitlyClosed = false;

function urlForToken(token: string): string {
  const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
  const host = window.location.host;
  return `${proto}//${host}/ws?token=${encodeURIComponent(token)}`;
}

function scheduleReconnect(): void {
  if (explicitlyClosed || reconnectTimer != null) return;
  attempts++;
  const delay = Math.min(30000, 1000 * Math.pow(2, attempts));
  reconnectTimer = window.setTimeout(() => {
    reconnectTimer = null;
    connect();
  }, delay);
}

function emit(event: string, payload: Record<string, unknown>): void {
  for (const h of handlers) {
    try {
      h(event, payload);
    } catch {
      // ignore handler errors
    }
  }
}

function startPing(): void {
  if (pingTimer != null) return;
  pingTimer = window.setInterval(() => {
    if (socket?.readyState === WebSocket.OPEN) {
      socket.send(JSON.stringify({ type: "ping" }));
    }
  }, 25000);
}

function stopPing(): void {
  if (pingTimer != null) {
    clearInterval(pingTimer);
    pingTimer = null;
  }
}

export function connect(): void {
  if (explicitlyClosed) return;
  const token = getAccessToken();
  if (!token) return;
  try {
    socket = new WebSocket(urlForToken(token));
  } catch {
    scheduleReconnect();
    return;
  }
  socket.addEventListener("open", () => {
    attempts = 0;
    startPing();
    // Re-subscribe
    for (const ch of subscribedChannels) {
      if (ch.startsWith("channel:")) {
        socket?.send(JSON.stringify({ type: "subscribe", channel_id: ch.slice("channel:".length) }));
      } else if (ch.startsWith("job:")) {
        socket?.send(JSON.stringify({ type: "subscribe", job_id: ch.slice("job:".length) }));
      }
    }
  });
  socket.addEventListener("message", (ev) => {
    try {
      const msg = JSON.parse(ev.data as string) as { event?: string; type?: string } & Record<string, unknown>;
      if (msg.event) {
        emit(msg.event, msg);
      }
    } catch {
      // ignore
    }
  });
  socket.addEventListener("close", () => {
    socket = null;
    stopPing();
    scheduleReconnect();
  });
  socket.addEventListener("error", () => {
    // close handler will reconnect
  });
}

export function disconnect(): void {
  explicitlyClosed = true;
  if (reconnectTimer != null) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
  stopPing();
  if (socket) {
    socket.close();
    socket = null;
  }
}

export function subscribeChannel(channelId: string): void {
  const key = `channel:${channelId}`;
  subscribedChannels.add(key);
  if (socket?.readyState === WebSocket.OPEN) {
    socket.send(JSON.stringify({ type: "subscribe", channel_id: channelId }));
  }
}

export function unsubscribeChannel(channelId: string): void {
  subscribedChannels.delete(`channel:${channelId}`);
  if (socket?.readyState === WebSocket.OPEN) {
    socket.send(JSON.stringify({ type: "unsubscribe", channel_id: channelId }));
  }
}

export function subscribeJob(jobId: string): void {
  subscribedChannels.add(`job:${jobId}`);
  if (socket?.readyState === WebSocket.OPEN) {
    socket.send(JSON.stringify({ type: "subscribe", job_id: jobId }));
  }
}

export function on(handler: WSHandler): () => void {
  handlers.add(handler);
  return () => {
    handlers.delete(handler);
  };
}

export function isConnected(): boolean {
  return socket?.readyState === WebSocket.OPEN;
}
